import { JobDestinationList } from "@pdfking/imposition"
import type { RGBColor } from "@pdfking/math"
import { AddMargins, ToRgb, ToSpatial } from "@pdfking/math"
import type { FilePlottingContext } from "@pdfking/pdfengine"
import { CreateRenderFunction } from "@pdfking/reactivity/preact"
import type { StringGUID } from "@pdfking/tfunc"
import { DeleteDuplicates, Items, Values } from "@pdfking/tfunc"

import { TintFilterID } from "#internal"
import type { MediaImage, RequestMedia } from "#public"

import { PlotMixin as mixin } from "../mixins/PlotMixin"
import { PlotMediaDef } from "./PlotMediaDef"

/**
 * The plot's pages, drawn nowhere: an svg of no size holding one def per
 * page, named by the page's guid, for every media showing the page to
 * `<use>` (see PlotImage). One element in the document per page, so the
 * browser draws it once, however many pages of the plot it lands on — and
 * only once a media showing it has scrolled into view. A split page is its
 * raster, sized as it is, the trim grown by the bleed, in the plot's pixels;
 * a plotted page is its own shapes over the same extent (see PlotMediaDef).
 * Beside them, one tint filter per overlay color (see TintFilterID), for every
 * overlay media to name rather than carry its own.
 */
export const MediaDefs = CreateRenderFunction({
  computed: {
    // every color an overlay can tint with: its print color, and its guide
    // color where it has one
    tints(): RGBColor[] {
      return [
        ...DeleteDuplicates(
          Values(JobDestinationList).flatMap(d =>
            d.print_mode === "overlay"
              ? [d.print_color, ...(d.guide_color ? [d.guide_color] : [])].map(c => ToRgb(c))
              : []
          ),
          TintFilterID
        ),
      ]
    },
  },
  mixins: [mixin] as const,
  props: {
    images: {} as Record<StringGUID, MediaImage>,
    // the media a plotted page's shapes show, out of its own context
    media_for: undefined as unknown as (context: FilePlottingContext) => RequestMedia,
  },
  render() {
    return (
      <svg aria-hidden className="absolute size-0" xmlns="http://www.w3.org/2000/svg">
        <defs>
          {this.tints.map(tint => (
            <filter id={TintFilterID(tint)} key={TintFilterID(tint)}>
              <feColorMatrix
                result="gray"
                type="matrix"
                values="0.299 0.587 0.114 0 0
                        0.299 0.587 0.114 0 0
                        0.299 0.587 0.114 0 0
                        0 0 0 1 0"
              />
              <feComponentTransfer result="inverted">
                <feFuncR tableValues="1 0" type="table" />
                <feFuncG tableValues="1 0" type="table" />
                <feFuncB tableValues="1 0" type="table" />
              </feComponentTransfer>
              <feColorMatrix
                result="tinted"
                type="matrix"
                values={`0 0 0 0 ${tint.red}
                         0 0 0 0 ${tint.green}
                         0 0 0 0 ${tint.blue}
                         ${tint.alpha} 0 0 0 0`}
              />
              <feComposite in="tinted" in2="SourceGraphic" operator="in" />
            </filter>
          ))}
          {(Items(this.images) as [StringGUID, MediaImage][]).map(([guid, image]) => {
            if ("plot" in image)
              return (
                <PlotMediaDef
                  context={this.context}
                  guid={guid}
                  key={guid}
                  media={image}
                  media_for={this.media_for}
                  plot={image.plot}
                />
              )
            const size = AddMargins(ToSpatial(image.trim), image.bleed)
            return (
              <image
                height={this.get_h(size)}
                href={image.url}
                id={guid}
                key={guid}
                width={this.get_w(size)}
              />
            )
          })}
        </defs>
      </svg>
    )
  },
})
